import React, { useEffect, useState, useMemo, useCallback } from "react";
import { EcommProductPrice } from "@arctheme-components/ecommerce/Price/Price";
import ImageComponent from "@project-stories/elements/Image/Image";
import ProductTileQuickAdd from "./ProductTileQuickAdd/ProductTileQuickAdd";
import { clsx } from "clsx";
import { getAlpineStore } from "../../../../entrypoints/theme";
import shopify from "../../../../js/shopify";

// TypeScript interfaces
interface ProductImage {
  src: string;
  alt?: string;
}

interface ProductSwatch {
  url: string;
  name: string;
  image?: string;
  color?: string;
  is_product: boolean;
  show_name?: boolean;
}

interface ProductTileProps {
  product: any;
  swatches?: any[];
  siblings?: any[];
  show_add?: boolean;
  show_price_inline?: boolean;
}

// Utility functions
const isSuitKit = (product: any): boolean => {
  return product?.type === 'Kit' || 
    product?.productType === 'Kit' ||
    product?.tags?.some((tag: string) => tag.includes('isSuitKit')) ||
    product?.template_suffix === 'suit-kit';
};

const getSuitComponents = (metafields: any) => [
  { key: "matching_jacket", label: "Jacket", product: metafields?.matching_jacket || metafields?.ap21?.matchingJacket?.value },
  { key: "matching_vest", label: "Waistcoat", product: metafields?.matching_vest || metafields?.ap21?.matchingVest?.value },
  { key: "matching_trouser", label: "Trousers", product: metafields?.matching_trouser || metafields?.ap21?.matchingTrouser?.value }
];

const calculatePriceFromProducts = (products: any[]): number => {
  return products.reduce((total, { product: refProduct }) => {
    if (!refProduct) return total;
    
    if (refProduct.price) {
      return total + (refProduct.price / 100);
    }
    if (refProduct.priceRange?.minVariantPrice?.amount) {
      return total + parseFloat(refProduct.priceRange.minVariantPrice.amount);
    }
    return total;
  }, 0);
};

const getHandlesFromProducts = (products: any[]): string[] => {
  return products
    .map(({ product: refProduct }) => refProduct?.handle)
    .filter(Boolean);
};

const createKeyValuePairs = (tags: string | string[]) => {
  const tagsArray = Array.isArray(tags) ? tags : [tags];
  return tagsArray.reduce((acc, tag) => {
    const [key, value] = tag.split(":");
    if (value) {
      if (acc[key]) {
        acc[key] = Array.isArray(acc[key]) ? [...acc[key], value] : [acc[key], value];
      } else {
        acc[key] = value;
      }
    }
    return acc;
  }, {} as Record<string, string | string[]>);
};

// Custom hooks
const useSuitPrice = (product: any) => {
  const [suitPrice, setSuitPrice] = useState<number | null>(null);

  const fetchProductPrices = useCallback(async (handles: string[]): Promise<number> => {
    let totalPrice = 0;
    
    const requests = handles.map(async (handle) => {
      try {
        const response = await fetch(`/products/${handle}.js`);
        if (response.ok) {
          const data = await response.json();
          return data?.variants?.[0]?.price || 0;
        }
      } catch (error) {
        console.warn(`Failed to fetch product data for handle ${handle}:`, error);
      }
      return 0;
    });

    const prices = await Promise.all(requests);
    return prices.reduce((sum, price) => sum + price, 0) / 100;
  }, []);

  const calculateSuitPrice = useCallback(async (metafields: any) => {
    const suitComponents = getSuitComponents(metafields);
    
    // Try to calculate from direct product references
    const directPrice = calculatePriceFromProducts(suitComponents);
    if (directPrice > 0) {
      return directPrice;
    }

    // Fallback to API calls using handles
    const handles = getHandlesFromProducts(suitComponents);
    if (handles.length > 0) {
      return await fetchProductPrices(handles);
    }

    return 0;
  }, [fetchProductPrices]);

  useEffect(() => {
    const fetchSuitPrice = async () => {
      if (!isSuitKit(product)) {
        return;
      }

      try {
        let totalPrice = 0;

        // First try with existing metafields
        if (product.metafields) {
          totalPrice = await calculateSuitPrice(product.metafields);
        }

        // If no price found and we have a product ID, fetch full product data
        if (totalPrice === 0 && product.id) {
          const productList = await shopify.getShopifyProductList([product.id]);
          if (productList?.[0]?.metafields) {
            totalPrice = await calculateSuitPrice(productList[0].metafields);
          }
        }

        if (totalPrice > 0) {
          setSuitPrice(totalPrice);
        }
      } catch (error) {
        console.error('Error calculating suit price:', error);
      }
    };

    fetchSuitPrice();
  }, [product, calculateSuitPrice]);

  return suitPrice;
};

const useProductSwatches = (product: any, siblings: any[], relatedProducts: any[]) => {
  return useMemo(() => {
    const productTags = createKeyValuePairs(product.tags || []);
    const swatches: ProductSwatch[] = [];
    
    if (!productTags?.colour) return swatches;

    const colors = Array.isArray(productTags.colour) ? productTags.colour : [productTags.colour];
    const currentProductHandle = product.handle;

    // Process siblings
    colors.forEach((colorHandle) => {
      const siblingProduct = siblings?.find((sibling) => sibling.handle === colorHandle);
      if (siblingProduct) {
        swatches.push({
          url: `/products/${siblingProduct.handle}`,
          name: product.title,
          image: siblingProduct.images?.find((image: any) => image.src?.match(/_s_|_s(\.|$)/))?.src ||
                 siblingProduct.images?.[siblingProduct.images?.length - 1]?.src,
          is_product: siblingProduct.handle === currentProductHandle,
        });
      }
    });

    // Process related products
    colors.forEach((colorHandle) => {
      const relatedProduct = relatedProducts?.find((related) => related.handle === colorHandle);
      if (relatedProduct && !swatches.some(s => s.url.includes(relatedProduct.handle))) {
        swatches.push({
          url: `/products/${relatedProduct.handle}`,
          name: product.title,
          image: relatedProduct.images?.edges?.find((edge: any) => 
                   edge.node.src?.match(/_s_|_s(\.|$)/))?.node.src ||
                 relatedProduct.images?.edges?.[relatedProduct.images.edges.length - 1]?.node.src,
          is_product: relatedProduct.handle === currentProductHandle,
        });
      }
    });

    return swatches;
  }, [product, siblings, relatedProducts]);
};

export const ProductTile: React.FC<ProductTileProps> = ({
  product,
  swatches = [],
  siblings = [],
  show_add = false,
  show_price_inline = false,
}) => {
  const store = getAlpineStore("modelview");
  const [modelView, setModelView] = useState(store.isModelView);
  const [isHovered, setIsHovered] = useState(false);
  const [relatedProducts, setRelatedProducts] = useState([]);
  
  const suitPrice = useSuitPrice(product);
  const productSwatches = useProductSwatches(product, siblings, relatedProducts);

  // Handle Algolia click tracking
  const handleProductClick = useCallback(() => {
    const windowWithAlgolia = window as any;
    
    if (windowWithAlgolia.algoliaProductClick && windowWithAlgolia.algoliaQueryID) {
      try {
        const productObjectID = product.objectID || product.id;
        windowWithAlgolia.algoliaProductClick(
          productObjectID,
          product.title,
          `/products/${product.handle}`,
          windowWithAlgolia.algoliaQueryID
        );
      } catch (error) {
        console.warn('Algolia click tracking failed:', error);
      }
    }
  }, [product]);

  useEffect(() => {
    const fetchProductSwatches = async () => {
      const relatedProductsShopify = await shopify.getRelatedProducts([product]);
      setRelatedProducts(relatedProductsShopify);
    };

    fetchProductSwatches();

    const handleModelViewToggle = (event: CustomEvent) => {
      setModelView(event.detail.isModelView);
    };

    window.addEventListener("modelview-toggled", handleModelViewToggle as EventListener);
    return () => {
      window.removeEventListener("modelview-toggled", handleModelViewToggle as EventListener);
    };
  }, [product]);

  // Process product images
  const processedImages = useMemo(() => {
    let images = product.media || product.images || [];
    
    // Filter out swatch images
    images = images.filter((image: any) => 
      image.src && !image.src.match(/_s_|_s(\.|$)/i)
    );

    // Fallback to featured image if no images
    if (!images.length && product.featured_image) {
      images = [{ src: product.featured_image }];
    }

    return images;
  }, [product]);

  // Calculate image indices based on model view and swatch detection
  const { imageIndex, hoverIndex } = useMemo(() => {
    const secondImageIsSwatch = /_s$/i.test(processedImages?.[1]?.src);
    
    if (modelView) {
      if (processedImages?.length > 1 && !secondImageIsSwatch) {
        return { imageIndex: 1, hoverIndex: 0 };
      }
      return { imageIndex: 0, hoverIndex: 0 };
    }
    
    return { 
      imageIndex: 0, 
      hoverIndex: processedImages?.length > 1 ? 1 : 0 
    };
  }, [modelView, processedImages]);

  // Calculate final product price
  const productPrice = suitPrice !== null ? suitPrice : (product?.price / 100 || 0);
  const comparePrice = product?.compare_at_price / 100;

  // Extract product tags and group fit
  const productTags = useMemo(() => createKeyValuePairs(product.tags || []), [product.tags]);
  const groupFit = productTags?.Group_Fit || productTags?.group_fit;

  // Get currency symbol
  const currencySymbol = (window as any).Alpine?.store('currency')?.getCurrencySymbol() || '$';

  // Utility function for image URLs
  const getImageUrl = (image: any) => {
    return typeof image === "object" && image?.src ? image.src : image;
  };

  return (
    <div 
      className={"theme__product-card"}
      data-product-id={product.objectID || product.id}
    >
      <div className={"productcard__content"}>
        <a
          href={`/products/${product.handle}`}
          className="relative w-full productcard__content__image"
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
          onClick={handleProductClick}
        >
          <ImageComponent
            classes={clsx(
              "product-image",
              isHovered &&
                processedImages.length > 1 &&
                "opacity-0 transition-opacity duration-500",
            )}
            imageUrl={getImageUrl(processedImages[imageIndex])}
          />
          <ImageComponent
            classes={clsx(
              "product-image absolute top-0 left-0",
              isHovered && processedImages.length > 1
                ? "opacity-100 transition-opacity duration-500"
                : "opacity-0",
            )}
            imageUrl={getImageUrl(processedImages[hoverIndex])}
          />
          
          {/* Quick Add Component */}
          {show_add && (
            <ProductTileQuickAdd product={product} />
          )}
        </a>
        <div className={"flex justify-between mt-[16px] flex-col lg:flex-row"}>
          <div>
            <a href={`/products/${product?.handle}`} onClick={handleProductClick}>
              <p className={"b2 text-[#54565A]"}>{product?.title}</p>
            </a>
            <p className={"bc uppercase text-[13px] font-[500] text-[#54565A]"}>
              {groupFit}
            </p>
          </div>
          <div
            className={
              "flex items-center justify-end flex-row-reverse lg:flex-col lg:items-end"
            }
          >
            {!show_price_inline && (
              <span className="b1 font-regular text-[#54565a]">
                <EcommProductPrice
                  price={`${currencySymbol}${productPrice.toFixed(2)}`}
                  compare_at_price={comparePrice}
                />
              </span>
            )}
            {comparePrice > 0 && productPrice < comparePrice && (
              <span className="text-[12px] line-through text-[#BBBBBD] mr-[8px] lg:mr-0">
                <div className="product__price" data-prodify-price-container="">
                  {`${currencySymbol}${comparePrice.toFixed(2)}`}
                </div>
              </span>
            )}
          </div>
        </div>

        {productSwatches.length > 0 && (
          <div className="product__swatches mt-[16px]">
            {productSwatches.map((swatch, index) => (
              <a
                key={`${swatch.url}-${index}`}
                href={swatch.url}
                className={clsx({
                  "border-b-2 border-[#54565A]": swatch.is_product,
                })}
              >
                <div className="product__swatch h-[16px] w-[16px] mb-[2px] cursor-pointer transition-all hover:opacity-70">
                  {swatch.color && (
                    <div className="mb-[2px]">
                      <div
                        className="product__swatch_color"
                        style={{ backgroundColor: swatch.color }}
                      />
                    </div>
                  )}
                  {swatch.image && (
                    <div className="h-[16px] w-[16px] mb-[2px] cursor-pointer">
                      <img
                        className="object-cover w-full h-full product__swatch_image"
                        src={swatch.image}
                        alt={swatch.name}
                        loading="lazy"
                      />
                    </div>
                  )}
                  {swatch.show_name && swatch.name}
                </div>
              </a>
            ))}
          </div>
        )}

        <div className="mt-[16px]">
          {productTags.automatic && (
            <p className="b3 text-[14px] font-[400] text-[#CDA176]">
              {productTags.automatic}
            </p>
          )}
          {productTags.preorder && (
            <p className="bg-[#54565a] text-white px-3 py-1 text-xs font-semibold inline-block uppercase">
              PRE-ORDER
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProductTile;
